import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { IMAGES } from "../lib/images";

const lines = ["Precisione", "Senza", "Compromessi"];

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1.05, delay: 0.5 + i * 0.13, ease: [0.19, 1, 0.22, 1] },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} id="top" data-testid="hero" className="relative h-[100svh] w-full overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <motion.img
          src={IMAGES.hero}
          alt="Lavorazione meccanica di precisione CNC"
          initial={{ scale: 1.1 }}
          animate={{ scale: 1 }}
          transition={{ duration: 8, ease: "easeOut" }}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/20 to-zinc-950/40" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 h-full max-w-[1600px] mx-auto px-6 md:px-12 flex flex-col justify-end pb-20 md:pb-28">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="flex items-center gap-3 mb-6"
        >
          <span className="w-8 h-px bg-red-600" />
          <span data-testid="hero-overline" className="text-xs md:text-sm font-mono uppercase tracking-[0.3em] text-red-600">
            Fornitore Ufficiale Ducati · ISO 9001
          </span>
        </motion.div>

        <h1 data-testid="hero-title" className="font-display font-black uppercase leading-[0.9] sm:leading-[0.85] tracking-tighter text-[2.6rem] sm:text-7xl md:text-8xl lg:text-[9rem]">
          {lines.map((line, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span custom={i} variants={lineVariants} initial="hidden" animate="visible" className="block">
                {line === "Senza" ? <span className="text-red-600">{line}</span> : line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.9 }}
          className="mt-8 max-w-xl text-base md:text-lg text-zinc-300 leading-relaxed"
        >
          Costruiamo attrezzature, prototipi e componenti meccanici di piccola e media serie per
          automotive, motorsport, aeronautico e difesa. Dal 1985, a Modena.
        </motion.p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 right-6 md:right-12 z-10 flex items-center gap-2 text-zinc-400"
      >
        <span className="text-xs font-mono uppercase tracking-widest">Scorri</span>
        <motion.div animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.6 }}>
          <ArrowDown size={16} />
        </motion.div>
      </motion.div>
    </section>
  );
};
